// Settings → Share your journal: private read-only links for a coach, mentor or accountability
// partner (share.js on the server; the page they open is SharePage.js). Admin-only preview until
// Admin → Features opens it.
import { useEffect, useState } from 'react';
import { api } from './api';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

const linkFor = (token) => `${window.location.origin}/share/${token}`;

export default function ShareLinks({ accounts, preview = false, onSessionExpired }) {
  const { t } = useI18n();
  const [links, setLinks] = useState(null);
  const [form, setForm] = useState({ label: '', accountIds: [], notes: false });
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [saving, setSaving] = useState(false);
  const active = accounts.filter((a) => !a.archived && !a.attemptOf);
  const fail = (err) => (err.status === 401 ? onSessionExpired() : setError(err.message));

  useEffect(() => { api('/share-links').then(setLinks).catch(fail); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function create(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const link = await api('/share-links', { method: 'POST', body: form });
      setLinks((l) => [link, ...l]);
      setForm({ label: '', accountIds: [], notes: false });
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  }
  async function turnOff(link) {
    if (!window.confirm(t('Turn this link off? Anyone who has it won’t be able to open it anymore.'))) return;
    try {
      await api(`/share-links/${link.id}`, { method: 'DELETE' });
      setLinks((l) => l.filter((x) => x.id !== link.id));
    } catch (err) {
      fail(err);
    }
  }
  function copy(token) {
    navigator.clipboard.writeText(linkFor(token)).then(() => { setCopied(token); setTimeout(() => setCopied(''), 2000); }).catch(() => {});
  }
  const toggleAccount = (id) => setForm((f) => ({ ...f, accountIds: f.accountIds.includes(id) ? f.accountIds.filter((x) => x !== id) : [...f.accountIds, id] }));
  const names = (ids) => (ids.length ? accounts.filter((a) => ids.includes(a._id)).map((a) => a.name).join(', ') : t('All accounts'));

  return (
    <section className={`settings-card${preview ? ' admin-preview-card' : ''}`} id="share">
      <h2>{t('Share your journal')} {preview && <span className="admin-preview-badge">Admin only</span>}</h2>
      <p className="settings-text">{t('Give a coach, mentor or accountability partner a private link to see your trades and results. They can look but can’t change anything, and you can turn a link off at any time.')}</p>

      <form className="share-form" onSubmit={create}>
        <label>{t('Who is it for? (optional)')}
          <input value={form.label} maxLength={60} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder={t('e.g. Coach Mike')} />
        </label>
        <fieldset className="share-accounts">
          <legend>{t('Accounts to show')}</legend>
          <p className="field-hint">{t('Leave all unticked to share every account.')}</p>
          {active.map((a) => (
            <label key={a._id} className="share-check">
              <input type="checkbox" checked={form.accountIds.includes(a._id)} onChange={() => toggleAccount(a._id)} /> {a.name}
            </label>
          ))}
        </fieldset>
        <label className="share-check">
          <input type="checkbox" checked={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.checked }))} />
          {t('Also share my journal notes (plans, reviews, emotions and trade notes)')}
        </label>
        <button type="submit" disabled={saving}>{saving ? t('Saving…') : t('Create link')}</button>
      </form>
      {error && <p className="error">{error}</p>}

      {links && links.length > 0 && (
        <ul className="share-list">
          {links.map((l) => (
            <li key={l.id}>
              <div>
                <strong>{l.label || t('Share link')}</strong>
                <small>{names(l.accountIds)} &bull; {l.notes ? t('with journal notes') : t('trades and results only')} &bull; {t(l.views === 1 ? 'opened {n} time' : 'opened {n} times', { n: l.views })}
                  {l.lastViewedAt && <> &bull; {t('last {day}', { day: new Date(l.lastViewedAt).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' }) })}</>}</small>
                <code>{linkFor(l.token)}</code>
              </div>
              <span className="share-actions">
                <button type="button" className="secondary small" onClick={() => copy(l.token)}>{copied === l.token ? t('Copied') : t('Copy link')}</button>
                <a className="secondary small button-link" href={`/share/${l.token}`} target="_blank" rel="noreferrer">{t('Preview')}</a>
                <button type="button" className="link-button biz-delete" onClick={() => turnOff(l)}>{t('Turn off')}</button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
